import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Stack,
} from "@mui/material";
import api from "../../utils/api";
import html2pdf from "html2pdf.js";
import ArchitecturalLetterTemplate from "./ArchitecturalLetterTemplate";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const sampleRequestDetails = {
  id: 123,
  requested_at: "2024-07-01",
  description: "Exterior paint color change request.",
  priority: "Medium",
  property_id: 42,
};

const sampleMember = {
  FirstName: "Jane",
  LastName: "Doe",
  EmailAddress: "jane.doe@example.com",
  FullAddress: "123 River View Dr, Waterlefe",
};

const ArchitecturalLetterTemplatesPage = () => {
  const [templates, setTemplates] = useState({});
  const [templateMeta, setTemplateMeta] = useState({});
  const [selectedLetter, setSelectedLetter] = useState("");
  const [viewMode, setViewMode] = useState("preview");
  const [loading, setLoading] = useState(true);
  const letterRef = useRef();
  const today = new Date().toLocaleDateString("en-US");

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api.get(`/architectural-letter-templates`);
        const templatesData = res.data;

        if (!templatesData || templatesData.length === 0) {
          setTemplates({});
          setTemplateMeta({});
          setSelectedLetter("");
          return;
        }

        const mappedTemplates = {};
        const metaData = {};

        templatesData.forEach((tpl) => {
          mappedTemplates[tpl.type] = tpl.content || "<p>No content</p>";
          metaData[tpl.type] = { id: tpl.id, name: tpl.name || tpl.type };
        });

        setTemplates(mappedTemplates);
        setTemplateMeta(metaData);
        setSelectedLetter(templatesData[0].type); // auto-select first
      } catch (err) {
        console.error("Error loading templates:", err);
        alert("Failed to load templates");
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleDownload = () => {
    if (!letterRef.current) return;
    html2pdf()
      .set({
        margin: 0.5,
        filename: `${selectedLetter}_Letter.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      })
      .from(letterRef.current)
      .save();
  };

  const handleSave = async () => {
    const { id, name } = templateMeta[selectedLetter] || {};
    const content = templates[selectedLetter];
    const type = selectedLetter;

    if (!type) return alert("Please select a letter type before saving.");

    try {
      if (id) {
        await api.put(`/architectural-letter-templates/${id}`, {
          name,
          type,
          content,
          is_default: 0,
        });
        alert("Template updated successfully.");
      } else {
        const res = await api.post(`/architectural-letter-templates`, {
          name,
          type,
          content,
          is_default: 0,
          created_by: null,
        });

        setTemplateMeta((prev) => ({
          ...prev,
          [type]: { ...prev[type], id: res.data.id },
        }));
        alert("Template created successfully.");
      }
    } catch (err) {
      console.error("Save failed:", err);
      alert("Failed to save template");
    }
  };

  const handleDelete = async () => {
    const { id } = templateMeta[selectedLetter] || {};
    if (!id) return alert("No template to delete.");

    try {
      await api.delete(`/architectural-letter-templates/${id}`);
      alert("Template deleted.");

      setTemplates((prev) => {
        const updated = { ...prev };
        delete updated[selectedLetter];
        return updated;
      });

      setTemplateMeta((prev) => {
        const updated = { ...prev };
        delete updated[selectedLetter];
        return updated;
      });

      const remainingKeys = Object.keys(templates).filter(
        (k) => k !== selectedLetter
      );
      setSelectedLetter(remainingKeys[0] || "");
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete template");
    }
  };

  const handleNewTemplate = () => {
    const newKey = `new_template_${Date.now()}`;
    setTemplates((prev) => ({ ...prev, [newKey]: "" }));
    setTemplateMeta((prev) => ({
      ...prev,
      [newKey]: { id: null, name: "New Template" },
    }));
    setSelectedLetter(newKey);
  };

  if (loading) return <CircularProgress sx={{ mt: 5 }} />;

  const letterTypes = Object.keys(templates);

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Architectural Letter Templates
      </Typography>

      <FormControl sx={{ mt: 2, minWidth: 250 }}>
        <InputLabel>Letter Type</InputLabel>
        <Select
          value={selectedLetter}
          label="Letter Type"
          onChange={(e) => setSelectedLetter(e.target.value)}
        >
          {letterTypes.length > 0 ? (
            letterTypes.map((key) => (
              <MenuItem key={key} value={key}>
                {templateMeta[key]?.name || key}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No templates available</MenuItem>
          )}
        </Select>
      </FormControl>

      {selectedLetter && (
        <>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Template Name"
              value={templateMeta[selectedLetter]?.name || ""}
              onChange={(e) =>
                setTemplateMeta((prev) => ({
                  ...prev,
                  [selectedLetter]: {
                    ...prev[selectedLetter],
                    name: e.target.value,
                  },
                }))
              }
              fullWidth
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, val) => val && setViewMode(val)}
            >
              <ToggleButton value="preview">Preview</ToggleButton>
              <ToggleButton value="edit">Edit HTML</ToggleButton>
              <ToggleButton value="rich">Rich Text</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {viewMode === "edit" ? (
            <TextField
              fullWidth
              multiline
              minRows={20}
              variant="outlined"
              value={templates[selectedLetter]}
              onChange={(e) =>
                setTemplates((prev) => ({
                  ...prev,
                  [selectedLetter]: e.target.value,
                }))
              }
              sx={{ mt: 3 }}
            />
          ) : viewMode === "rich" ? (
            <Box sx={{ mt: 3 }}>
              <ReactQuill
                theme="snow"
                value={templates[selectedLetter]}
                onChange={(value) =>
                  setTemplates((prev) => ({
                    ...prev,
                    [selectedLetter]: value,
                  }))
                }
                style={{ height: "400px", marginBottom: "40px" }}
              />
            </Box>
          ) : (
            <Paper
              elevation={3}
              sx={{
                mt: 3,
                p: 3,
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
            >
              <div ref={letterRef}>
                <ArchitecturalLetterTemplate
                  type={templateMeta[selectedLetter]?.name || selectedLetter}
                  content={templates[selectedLetter]}
                  member={sampleMember}
                  requestDetails={sampleRequestDetails}
                  date={today}
                />
              </div>
            </Paper>
          )}

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={handleDownload}>
              Download PDF
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save Template
            </Button>
            <Button color="secondary" onClick={handleNewTemplate}>
              New Template
            </Button>
            <Button color="error" onClick={handleDelete}>
              Delete Template
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default ArchitecturalLetterTemplatesPage;
